import { Component, type ReactNode, type ErrorInfo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional section name for localized error messages */
  section?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Map of localized section error keys. */
const SECTION_KEYS = new Set(['study', 'billing', 'admin', 'profile', 'auth']);

/** Sub-component allows hook-based i18n inside a class boundary. */
function SectionErrorFallback({ section, onRetry }: { section: string; onRetry: () => void }) {
  const { t } = useTranslation();
  const message = SECTION_KEYS.has(section)
    ? t(`components.errorBoundary.sections.${section}` as const)
    : t('components.errorBoundary.fallbackSection', { section });

  return (
    <div className="flex items-center justify-center p-8" role="alert">
      <div className="max-w-md rounded-2xl border border-slate-200/70 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-card-dark">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15">
          <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <h2 className="mb-2 text-lg font-extrabold tracking-[-0.01em] text-slate-900 dark:text-white">
          {t('components.errorBoundary.sectionTitle')}
        </h2>
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          {message}
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={onRetry}
            className="rounded-[11px] bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] transition-colors hover:bg-blue-700"
          >
            {t('components.errorBoundary.tryAgain')}
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-[11px] border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
          >
            {t('components.errorBoundary.reloadPage')}
          </button>
        </div>
      </div>
    </div>
  );
}

function GlobalErrorFallback({ error }: { error: Error | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas dark:bg-canvas-dark" role="alert">
      <div className="rounded-2xl border border-slate-200/70 bg-white p-10 text-center shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-card-dark">
        <h1 className="mb-2 text-2xl font-extrabold tracking-[-0.02em] text-slate-900 dark:text-white">{t('components.errorBoundary.globalTitle')}</h1>
        <p className="mb-4 text-slate-500 dark:text-slate-400">{error?.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-[11px] bg-blue-600 px-4 py-2 font-semibold text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] transition-colors hover:bg-blue-700"
        >
          {t('components.errorBoundary.reload')}
        </button>
      </div>
    </div>
  );
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.section ?? 'global', error, info);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { section } = this.props;

      // Per-section boundary shows inline error; global shows full-screen
      if (section) {
        return <SectionErrorFallback section={section} onRetry={this.handleRetry} />;
      }

      // Global fallback (full-screen)
      return <GlobalErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}
